import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Trophy, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface Option {
  ticker: string;
  name: string;
  tag: string; // short tag like "Mega-cap AI", "EV", "Crypto"
}

// Curated multiple-choice options spanning sectors + risk profiles
const OPTIONS: Option[] = [
  { ticker: "NVDA", name: "Nvidia", tag: "AI chips" },
  { ticker: "AAPL", name: "Apple", tag: "Mega-cap tech" },
  { ticker: "TSLA", name: "Tesla", tag: "EV | high beta" },
  { ticker: "MSFT", name: "Microsoft", tag: "Cloud | AI" },
  { ticker: "AMZN", name: "Amazon", tag: "E-commerce | AWS" },
  { ticker: "GOOGL", name: "Alphabet", tag: "Search | AI" },
  { ticker: "META", name: "Meta", tag: "Social | AI" },
  { ticker: "BTC-USD", name: "Bitcoin", tag: "Crypto | volatile" },
];

interface Pick { ticker: string; allocation: number }
interface GradeResult {
  picks: { ticker: string; verdict: "Smart" | "Mixed" | "Risky"; reasoning: string }[];
  overall_score: number;
  ai_take: string;
  quotes: { ticker: string; name: string; price: number; change_pct: number }[];
}

interface Props { onGetStarted?: () => void }

const StockQuizWidget = ({ onGetStarted }: Props) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [allocation, setAllocation] = useState(50);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GradeResult | null>(null);

  const toggle = (ticker: string) => {
    if (selected.includes(ticker)) {
      setSelected(selected.filter((t) => t !== ticker));
    } else if (selected.length < 2) {
      setSelected([...selected, ticker]);
    } else {
      toast.message("Pick only 2 stocks", { description: "Tap one of your selections to swap it out." });
    }
  };

  const submit = async () => {
    if (selected.length !== 2) return;
    setLoading(true);
    setResult(null);
    try {
      const picks: Pick[] = [
        { ticker: selected[0], allocation },
        { ticker: selected[1], allocation: 100 - allocation },
      ];
      const { data, error } = await supabase.functions.invoke("quiz-grader", {
        body: { picks, amount: 10000 },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setResult(data as GradeResult);
    } catch (e: any) {
      toast.error(e?.message || "Could not grade your picks. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setSelected([]); setResult(null); setAllocation(50); };

  const verdictStyle = (v: string) => {
    if (v === "Smart") return { color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", icon: CheckCircle2 };
    if (v === "Risky") return { color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/30", icon: AlertTriangle };
    return { color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", icon: Sparkles };
  };

  return (
    <section className="max-w-2xl mx-auto px-2 py-6 text-start">
      <div className="rounded-2xl border border-gold/20 bg-card/60 backdrop-blur-sm p-5 sm:p-6 shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-4 h-4 text-gold" />
          <h2 className="text-base sm:text-lg font-display font-bold text-foreground">
            $10,000 Stock Quiz
          </h2>
        </div>
        <p className="text-xs sm:text-sm text-muted-foreground mb-4">
          You have $10,000 today. Pick any 2 from below, split the money, and see how AI grades your portfolio.
        </p>

        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {OPTIONS.map((o) => {
                  const isSelected = selected.includes(o.ticker);
                  const idx = selected.indexOf(o.ticker);
                  return (
                    <button
                      key={o.ticker}
                      type="button"
                      onClick={() => toggle(o.ticker)}
                      className={`relative rounded-xl border px-2.5 py-3 text-start transition-all ${
                        isSelected
                          ? "border-gold bg-gold/10 ring-2 ring-gold/30"
                          : "border-border bg-secondary/40 hover:border-gold/40 hover:bg-secondary/70"
                      }`}
                    >
                      {isSelected && (
                        <span className="absolute -top-2 -end-2 w-5 h-5 rounded-full bg-gold text-primary-foreground text-[10px] font-bold flex items-center justify-center shadow">
                          {idx + 1}
                        </span>
                      )}
                      <div className="font-display font-bold text-foreground text-sm leading-tight">{o.ticker}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{o.name}</div>
                      <div className="text-[10px] text-gold/80 mt-1 truncate">{o.tag}</div>
                    </button>
                  );
                })}
              </div>

              {selected.length === 2 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5 font-display">
                    <span><span className="text-gold font-bold">{selected[0]}</span>: {allocation}% (${((allocation / 100) * 10000).toFixed(0)})</span>
                    <span><span className="text-gold font-bold">{selected[1]}</span>: {100 - allocation}% (${(((100 - allocation) / 100) * 10000).toFixed(0)})</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={90}
                    step={5}
                    value={allocation}
                    onChange={(e) => setAllocation(Number(e.target.value))}
                    className="w-full accent-gold"
                  />
                  <Button variant="gold" size="lg" className="w-full mt-4" onClick={submit} disabled={loading}>
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Grading…</> : <><Sparkles className="w-4 h-4" /> Grade my picks</>}
                  </Button>
                </motion.div>
              )}

              {selected.length < 2 && (
                <p className="text-xs text-muted-foreground/70 text-center mt-4 font-display">
                  Select {2 - selected.length} more {selected.length === 1 ? "pick" : "picks"} to continue
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div key="result" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs text-muted-foreground font-display">Portfolio Score</div>
                  <div className="text-3xl font-display font-bold text-gradient-gold">
                    {result.overall_score}<span className="text-base text-muted-foreground">/100</span>
                  </div>
                </div>
                <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                  Try again
                </button>
              </div>

              <div className="space-y-2.5">
                {result.picks.map((p) => {
                  const s = verdictStyle(p.verdict);
                  const Icon = s.icon;
                  const quote = result.quotes.find((q) => q.ticker === p.ticker);
                  return (
                    <div key={p.ticker} className={`rounded-xl border p-3 ${s.bg}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${s.color}`} />
                          <span className="font-display font-bold text-foreground">{p.ticker}</span>
                          {quote && <span className="text-xs text-muted-foreground">${quote.price?.toFixed(2)}</span>}
                        </div>
                        <span className={`text-xs font-bold ${s.color}`}>{p.verdict}</span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{p.reasoning}</p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 rounded-xl border border-gold/30 bg-gold/5 p-3">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground font-display">{result.ai_take}</p>
                </div>
              </div>

              {onGetStarted && (
                <Button variant="gold" size="lg" className="w-full mt-4" onClick={onGetStarted}>
                  Get a real AI Buy signal <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default StockQuizWidget;
